/**
 * Displays OTS information. Could be generalised to display arbitrary textual data.
 */
import _ from 'underscore';
import {once} from "@/app/utils/Functions";
import {errorHelper} from "@/dashboards/DashboardUtils";
import {splitUnique} from "@/app/utils/StringUtils";
import VuexStore from "@/store/vuex/VuexStore";
import {currentAccountCode} from "@/app/utils/Account";
import {beansprout} from "@/store/Services";
import {findAllNodes, parseFilterString} from "@/dashboards/filter/FilterParser";
import {AndNode, MentionQLexer} from "@/mentionq/mentionq";
import {SOCIAL_NETWORKS} from "@/app/utils/SocialNetworks";

Beef.module("Widget.TextOnlineProfile").addInitializer(function(startupOptions) {

    this.View = Beef.BoundItemView.extend({

        template: require("@/dashboards/widgets/onlineprofile/text/TextOnlineProfile.handlebars"),

        //-------------------------------------------

        modelEvents: {
            "change:_effectiveFilter":   "refresh",
            "change:profiles": "refresh",
            "change:width": "widthHeightChanged",
            "change:height": "widthHeightChanged"
        },

        //-------------------------------------------

        imageExportDisabled: true,
        svgExportDisabled: true,

        //-------------------------------------------

        regions: {
            "listRegion": ".online-profile-list-region"
        },

        //-------------------------------------------

        initialize: function() {
            this.accountCode = this.model.getAncestorProperty('accountCode');
            this.listenTo(this.model.generalData, "change:data", this.onRender, this);
            this.refresh();
        },

        //-------------------------------------------

        getCsvEntries: function(filename) {

            var url = "/rest/" + this.getProfileUrl() + ".csv?filter=" + encodeURIComponent(this.getParameters().filter) ;

            return {
                filename: filename,
                urls:     [{
                    url: url,
                    name: 'profiles.csv'
                }]
            }
        },

        getProfiles: function(){
            var profiles = this.model.get('profiles');
            profiles = profiles ?  _(splitUnique(profiles)).map(function(d) { return parseInt(d); }) : [];
            return profiles;
        },

        getProfileUrl: function() {
            var profiles = this.getProfiles();
            var code = this.model.getAncestorProperty('accountCode');
            var profileString = profiles.length ? "[" + profiles.join(',') + "]" : '';

            return "accounts/" + code + "/online-profiles/" + profileString;
        },

        getParameters: function() {
            var profiles = this.getProfiles();
            var parameters = {filter: this.model.get('_effectiveFilter')};

            if(profiles.length == 1){
                parameters.include = "networkInfo";
            }

            return parameters;
        },

        refresh: async function() {
            if (!this.model.get('_effectiveFilter')) return;

            let filter = this.model.get('_effectiveFilter');

            var selectedProfileIds = this.getProfiles();
            if (!selectedProfileIds || !selectedProfileIds.length) {
                this.model.generalData.set("_message", "Please select a profile");
                return;
            }

            this.model.generalData.set('_loading', true);

            const competitorProfiles = VuexStore.getters["competitorProfiles/getCompetitorProfiles"];
            const ownProfiles = VuexStore.getters["competitorProfiles/getOwnProfiles"];
            if ((!competitorProfiles || competitorProfiles.length === 0)
                && (!competitorProfiles || competitorProfiles.length === 0)) {
                // the parent component triggers competitorProfiles/refreshCompetitorProfiles, this function needs to wait for the data to be loaded
                setTimeout(() => this.refresh());
                return;
            }

            const accountCode = currentAccountCode();
            if (accountCode === 'BEAL65AA' || accountCode === 'BECH69AA') {
                await VuexStore.dispatch("competitorProfiles/refreshProfilesLatest");
                let competitorProfilesWithStats = VuexStore.getters["competitorProfiles/getLatestCompetitorProfilesWithStats"];

                const matchedSelectedProfilesWithStats = [];

                selectedProfileIds.forEach(profile => {
                    const competitorProfileWithStats = competitorProfilesWithStats
                        .find(profileWithStats => profileWithStats.brandId === profile);

                    if(competitorProfileWithStats){
                        matchedSelectedProfilesWithStats.push(competitorProfileWithStats);
                        competitorProfileWithStats.isBeansproutMetrics = true;
                    }
                })

                const publishedNodes = findAllNodes(parseFilterString(filter), function (node) {
                    return node.operandType == MentionQLexer.PUBLISHED;
                });

                filter = _(publishedNodes).reduce(function (lhs, rhs) {
                    return new AndNode(lhs, rhs);
                }).toString();

                const selectedProfilesHandleIds = matchedSelectedProfilesWithStats.map(profileWithStats => profileWithStats.handle);

                let res;
                try {
                    res = await beansprout.post('/rest/profile-stats-timeline',
                        {'handleIds': selectedProfilesHandleIds, filter});
                } catch (error) {
                    console.warn('An error retrieving profile-stats-timeline', error);
                    this.model.generalData.set("_message", "Metrics for profile not found");
                    this.model.generalData.set('_loading', false);
                    return;
                }

                if (res.status !== 200) {
                    this.model.generalData.set('_message', "An error occurred while fetching timeline metrics. Please" +
                        " try again later or contact support if the problem persists.");
                    return;
                }

                if (Object.keys(res.data).length !== 0) {

                    const graphData = [];

                    for (let handleId in res.data) {
                        const firstStat = res.data[handleId][0];
                        const lastIndex = res.data[handleId].length - 1;
                        const lastStat = res.data[handleId][lastIndex];

                        if (lastStat.likeCount != null && firstStat.likeCount != null) {
                            lastStat.likesDelta = lastStat.likeCount - firstStat.likeCount;
                            lastStat.likesPercent = Math.abs(lastStat.likesDelta / firstStat.likeCount * 100);
                        }

                        // Calculate follower count delta (both Facebook and Instagram)
                        if (lastStat.followerCount != null && firstStat.followerCount != null) {
                            lastStat.followerDelta = lastStat.followerCount - firstStat.followerCount;
                            lastStat.followerPercent = Math.abs(lastStat.followerDelta / firstStat.followerCount * 100);
                        }

                        if (firstStat.channel.toLowerCase() === SOCIAL_NETWORKS.YOUTUBE.toLowerCase()) {
                            lastStat.videoCountDelta = lastStat.metrics.video_count - firstStat.metrics.video_count;
                            lastStat.videoCountPercent = Math.abs(lastStat.videoCountDelta / firstStat.metrics.video_count * 100);
                            lastStat.videoCount = lastStat.metrics.video_count;
                        }

                        if (firstStat.channel.toLowerCase() === "twitter") {
                            lastStat.postCount = lastStat.metrics.post_count;
                            lastStat.postDelta = lastStat.metrics.post_count - firstStat.metrics.post_count;
                            lastStat.postPercent = Math.abs(lastStat.postDelta / firstStat.metrics.post_count * 100);

                            lastStat.followingCount = lastStat.metrics.following_count;
                            lastStat.followingDelta = lastStat.metrics.following_count - firstStat.metrics.following_count;
                            lastStat.followingPercent = Math.abs(lastStat.followingDelta / firstStat.metrics.following_count * 100);
                        }

                        lastStat.isBeansproutMetrics = true;

                        // Ensure we have the profile information for tooltip
                        lastStat.profile = {
                            handle: lastStat.name,
                            name: lastStat.name,
                            type: lastStat.channel
                        };

                        graphData.push(lastStat)
                    }

                    this.model.generalData.set('data', graphData);
                    this.model.generalData.set('_loading', false);
                }
            } else {
                // continue as normal
                var that = this;
                this.model.getSectionModel().view.getJsonFromSnoek(this.getProfileUrl(), this.getParameters(), _(function(data) {
                    if (data && !Array.isArray(data)) {
                        data = [data];
                    }

                    that.model.generalData.set('data', data);

                    if (data.length === 1) {
                        var profileData = data[0];
                        var callFilter = that.model.get('_effectiveFilter');
                        callFilter = "(" + callFilter + ") AND AuthorID IS '" +
                            Beef.AuthorIdPicker.toNiceAuthorId(profileData.authorId, profileData.handle, profileData.name) + "'";

                        that.model.getSectionModel().view.getJsonFromGrouse('/v4/accounts/' + this.accountCode + '/mentions/count',
                                { filter: callFilter, select: "mentionCount,totalOTS,totalAVE,totalEngagement,totalReshareCount,totalReplyCount" })
                            .then(
                            function (data) {
                                data.count = data.mentionCount;
                                data.ots = data.totalOTS;
                                data.ave = data.totalAVE;
                                data.engagement = data.totalEngagement;
                                data.reshareCount = data.totalReshareCount;
                                data.replyCount = data.totalReplyCount;
                                Object.assign(that.model.generalData.get('data')[0], data);
                                that.model.generalData.set('_loading', false);
                                that.onRender();
                            }, once(e => errorHelper(this.model, e)));
                    }
                    else {
                        that.model.generalData.set('_loading', false);
                    }
                }).bind(this)).fail(error => errorHelper(this.model, error));
            }


        },

        onRender: function() {
            if (!this.model.get('_effectiveFilter')) return;

            if (!this.list) {
                var collection = new Beef.Widgets.TextOnlineProfileList.Collection();
                this.list =  new Beef.Widgets.TextOnlineProfileList.View({
                    collection: collection,
                    code: this.model.getAncestorProperty('accountCode'),
                    filter: this.model.get('_effectiveFilter') || null
                });
                this.listRegion.show(this.list);
            }

            var data = this.model.generalData.get('data');
            var width = this.model.get('width'),
                height = this.model.get('height'),
                filter = this.getParameters().filter;

            this.list.collection.reset(_(data).map(function(d) {
                d.width = width;
                d.height = height;
                d.filter = filter;
                return new Beef.Widgets.TextOnlineProfileItem.Model(d);
            }));
            this.model.generalData.set('_completed', true);
        },

        widthHeightChanged: function() {
            if (this.list) {
                this.list.collection.trigger('redraw', {width: this.model.get('width'), height: this.model.get('height')});

                var width = this.model.get('width');
                var height = this.model.get('height');

                _(this.list.collection.models).each(function(m) {
                    m.set({
                        width: width,
                        height: height
                    })
                });
            }
        }


    });
});